import { Location } from 'history';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { memo, useContext, useEffect } from 'react';

import { setGaUserDimensions, trackPageView } from '~/lib/analytics';
import { useIdentity } from '~/lib/auth';
import { useLocationEffect } from '~/lib/hooks/useLocationEffect';

import { useSetIdentity } from '../auth/authProvider';
import { useCurrentTeacher } from '../hooks/useCurrentTeacher';
import { toLDUser } from '../launchDarkly';
import { LDAnonymousFallbackContext } from '../launchDarkly/provider';

import { updateIntercomPageView } from './intercom';
import { setIntercomUser } from './intercom/setIntercomUser';
import { setSentryUser } from './sentry';

const isProduction = process.env.NODE_ENV === 'production';
const isGAToken = !!process.env.GA_MEASUREMENT_ID;
const trackLocation = (location: Location): void => {
  if (isProduction || isGAToken) {
    trackPageView(location);
    updateIntercomPageView();
  }
};

export const Config = memo(function Config(): null {
  const { user } = useIdentity();
  const { data: teacher } = useCurrentTeacher();
  const setIdentity = useSetIdentity();
  const ldClient = useLDClient();
  const usingAnonymousFallback = useContext(LDAnonymousFallbackContext);

  useEffect(() => {
    const ldUser = toLDUser(teacher);

    if (!ldUser || !ldClient) {
      return;
    }

    let cancelled = false;

    const identifyTeacher = (): void => {
      if (cancelled) {
        return;
      }

      ldClient.identify(ldUser, undefined, err => {
        if (err || cancelled) {
          return;
        }

        setIdentity(state => ({ ...state, ldUser }));
      });
    };

    if (!usingAnonymousFallback) {
      identifyTeacher();

      return () => {
        cancelled = true;
      };
    }

    void Promise.resolve(ldClient.waitForInitialization())
      .then(identifyTeacher)
      .catch(identifyTeacher);

    return () => {
      cancelled = true;
    };
  }, [ldClient, setIdentity, teacher, usingAnonymousFallback]);

  useEffect(() => {
    setGaUserDimensions({
      dimension1: user?.profile.sub ?? null,
      dimension2: '',
      dimension3: user?.profile.idp ?? null,
      userId: user?.profile.sub ?? null,
    });
    setSentryUser(
      teacher
        ? {
            displayName: `${teacher.firstName} ${teacher.lastName}`,
            email: teacher.email,
            id: teacher.id,
            idp: user?.profile.idp,
            username: teacher.username,
          }
        : null,
    );
    setIntercomUser(user);
  }, [teacher, user]);
  useLocationEffect(trackLocation, []);

  return null;
});
